import React, { useState, useRef } from 'react';
import { FaSync, FaUpload, FaCheckCircle, FaExclamationTriangle, FaLock, FaDatabase } from 'react-icons/fa';
import './SyncPage.css';


// ─────────────────────────────────────────────────────────────────────────────
// Main page component
// ─────────────────────────────────────────────────────────────────────────────

const SyncPage = () => {
  const [authState, setAuthState]       = useState('idle'); // 'idle' | 'checking' | 'ok' | 'error'
  const [authHeader, setAuthHeader]     = useState(null);
  const [authError, setAuthError]       = useState('');
  const [username, setUsername]         = useState('');
  const [appPassword, setAppPassword]   = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [syncState, setSyncState]       = useState('idle'); // 'idle' | 'syncing' | 'done' | 'error'
  const [result, setResult]             = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef();

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthState('checking');
    setAuthError('');
    try {
      // Proxy through Vercel to avoid CORS preflight on the Authorization header.
      const res = await fetch('/api/wp-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password: appPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAuthState('idle');
        setAuthError(data.message || 'Invalid credentials.');
        return;
      }
      // Build the header locally so it can be forwarded to wp-sync.
      const encoded = btoa(`${username}:${appPassword}`);
      setAuthHeader(`Basic ${encoded}`);
      setAuthState('ok');
    } catch {
      setAuthState('idle');
      setAuthError('Could not reach the server.');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.sqlite')) {
      alert('Please select a .sqlite file exported from MacDive.');
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handleSync = async () => {
    if (!selectedFile) return;
    setSyncState('syncing');
    setResult(null);
    setErrorMessage('');

    try {
      const body = new FormData();
      body.append('sqlite_file', selectedFile);

      // Proxy through Vercel to avoid CORS preflight on the multipart POST.
      const res = await fetch('/api/wp-sync', {
        method: 'POST',
        headers: { Authorization: authHeader },
        body,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || `Server error (HTTP ${res.status})`);
      }
      setResult(data);
      setSyncState('done');
    } catch (err) {
      setErrorMessage(err.message || 'An unexpected error occurred.');
      setSyncState('error');
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setSyncState('idle');
    setResult(null);
    setErrorMessage('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Render states ──────────────────────────────────────────────────────────

  if (authState === 'idle' || authState === 'checking') {
    return (
      <div className="sync-page">
        <div className="sync-header">
          <FaLock className="sync-header-icon" />
          <h1>Admin Sign In</h1>
          <p className="sync-subtitle">
            Enter your WordPress admin credentials to access the sync tool.
            Use an <strong>Application Password</strong> — not your WP login password.
          </p>
        </div>
        <form className="sync-login-form" onSubmit={handleLogin}>
          <div className="sync-login-field">
            <label htmlFor="sync-username">WordPress username</label>
            <input
              id="sync-username"
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              autoComplete="username"
            />
          </div>
          <div className="sync-login-field">
            <label htmlFor="sync-apppassword">
              Application password
              <a
                href="/wp-admin/profile.php#application-passwords-section"
                target="_blank"
                rel="noreferrer"
                className="sync-login-help"
              >
                Generate one in WP Admin →
              </a>
            </label>
            <input
              id="sync-apppassword"
              type="password"
              value={appPassword}
              onChange={e => setAppPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
            />
          </div>
          {authError && <p className="sync-login-error">{authError}</p>}
          <button className="sync-btn" type="submit" disabled={authState === 'checking'}>
            {authState === 'checking'
              ? <><div className="sync-spinner-sm" /> Verifying…</>
              : <><FaLock /> Sign in</>
            }
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="sync-page">
      <div className="sync-header">
        <FaDatabase className="sync-header-icon" />
        <h1>MacDive Sync</h1>
        <p className="sync-subtitle">
          Upload a <code>MacDive.sqlite</code> export to sync new and updated dives
          into the Nothing But Scuba dive database.
        </p>
      </div>

      {syncState !== 'done' && (
        <div className="sync-upload-panel">
          <div className="sync-file-row">
            <label className="sync-file-label" htmlFor="sqlite-upload">
              <FaUpload />
              <span>{selectedFile ? selectedFile.name : 'Choose MacDive.sqlite…'}</span>
            </label>
            <input
              id="sqlite-upload"
              ref={fileInputRef}
              type="file"
              accept=".sqlite"
              onChange={handleFileChange}
              className="sync-file-input"
            />
          </div>

          <button
            className="sync-btn"
            onClick={handleSync}
            disabled={!selectedFile || syncState === 'syncing'}
          >
            {syncState === 'syncing' ? (
              <><div className="sync-spinner-sm" /> Syncing…</>
            ) : (
              <><FaSync /> Sync Now</>
            )}
          </button>

          {syncState === 'syncing' && (
            <p className="sync-progress-note">
              Reading database and comparing records — this may take a few seconds…
            </p>
          )}
        </div>
      )}

      {syncState === 'error' && (
        <div className="sync-error-banner">
          <FaExclamationTriangle />
          <span><strong>Sync failed:</strong> {errorMessage}</span>
          <button onClick={handleReset} className="sync-reset-btn">Try again</button>
        </div>
      )}

      {syncState === 'done' && result && (
        <SyncResults result={result} onReset={handleReset} />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Results panel
// ─────────────────────────────────────────────────────────────────────────────

const SyncResults = ({ result, onReset }) => {
  const { summary, changes, errors } = result;
  const totalChanged =
    (summary.dive_sites.added + summary.dive_sites.updated) +
    (summary.dives.added + summary.dives.updated) +
    summary.dive_types.added;

  return (
    <div className="sync-results">
      <div className="sync-results-header">
        <FaCheckCircle className="sync-success-icon" />
        <h2>Sync complete</h2>
        <button onClick={onReset} className="sync-reset-btn">Sync another file</button>
      </div>

      <div className="sync-summary-cards">
        <SummaryCard label="Dive Sites" data={summary.dive_sites} hasUpdated />
        <SummaryCard label="Dives"      data={summary.dives}      hasUpdated />
        <SummaryCard label="Dive Types" data={summary.dive_types} hasUpdated={false} />
      </div>

      {totalChanged === 0 && errors.length === 0 && (
        <p className="sync-no-changes">Everything is already up to date — no changes were needed.</p>
      )}

      {changes.length > 0 && (
        <div className="sync-log">
          <h3>Change log <span className="sync-log-count">({changes.length})</span></h3>
          <ul className="sync-log-list">
            {changes.map((entry, i) => (
              <ChangeEntry key={i} entry={entry} />
            ))}
          </ul>
        </div>
      )}

      {errors.length > 0 && (
        <div className="sync-warnings">
          <h3>Warnings</h3>
          <ul>
            {errors.map((e, i) => (
              <li key={i}><FaExclamationTriangle /> {e}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const SummaryCard = ({ label, data, hasUpdated }) => (
  <div className="sync-summary-card">
    <h3>{label}</h3>
    <div className="sync-stat-row">
      {data.added > 0
        ? <span className="sync-stat sync-stat-added">{data.added} added</span>
        : <span className="sync-stat sync-stat-zero">{data.added} added</span>
      }
      {hasUpdated && (
        data.updated > 0
          ? <span className="sync-stat sync-stat-updated">{data.updated} updated</span>
          : <span className="sync-stat sync-stat-zero">{data.updated} updated</span>
      )}
      <span className="sync-stat sync-stat-unchanged">{data.unchanged} unchanged</span>
    </div>
  </div>
);

const ChangeEntry = ({ entry }) => {
  let label;
  if (entry.type === 'dive') {
    label = `Dive #${entry.dive_number}`;
    if (entry.date) label += ` (${entry.date})`;
    if (entry.site) label += ` — ${entry.site}`;
  } else if (entry.type === 'dive_site') {
    label = `Site: ${entry.name}`;
  } else {
    label = `Type: ${entry.name}`;
  }

  return (
    <li className={`sync-log-entry sync-log-${entry.action}`}>
      <span className="sync-log-badge">{entry.action === 'added' ? 'NEW' : 'UPD'}</span>
      <span className="sync-log-label">{label}</span>
      {entry.fields_changed && entry.fields_changed.length > 0 && (
        <span className="sync-log-fields">
          {entry.fields_changed.join(', ')}
        </span>
      )}
    </li>
  );
};

export default SyncPage;
